import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { post } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { useTranslation } from "@localization/useTranslation";
import { toLevelOptions, useMitigationPlanForm } from "../treatmentDialogs/useMitigationPlanForm";
import { IBulkMitigationDialog } from "./BulkMitigationDialog";
import { BulkMitigationRequest, BulkMitigationResult } from "./BulkMitigationDialog.types";

export function useBulkMitigationDialog(props: IBulkMitigationDialog) {
    const { translateString } = useTranslation();
    const snackbar = useDotLegalSnackbar();

    // The overview selection is the single source of truth: the dialog renders props.scenarios live and removing
    // here calls back to the overview, which unchecks the row and re-renders with one fewer scenario.
    const scenarios = props.scenarios;

    const planForm = useMitigationPlanForm();

    // Every selected scenario sits in the same risk area, so the expected-after-mitigation levels are identical
    // across them — take them from the first row.
    const levelSource = scenarios[0];
    const consequenceOptions = toLevelOptions(levelSource?.consequenceLevels ?? []);
    const probabilityOptions = toLevelOptions(levelSource?.probabilityLevels ?? []);

    const saveMutation = useDotLegalMutation((request: BulkMitigationRequest) => post<BulkMitigationResult>("/risk-assessments/bulk-mitigate", request), {
        onSuccess: (response: Result<BulkMitigationResult>) => {
            if (response.success()) {
                snackbar.show(translateString("bulkMitigationCreated", { count: response.value().created }));
                props.onSaved();
                props.onClose();
            }
        },
    });

    const isValid = !planForm.validation.anyHasErrors && scenarios.length > 0;

    const onSave = () => {
        if (planForm.validation.anyHasErrors) {
            planForm.validation.setShowErrors(true);
            return;
        }

        saveMutation.mutateAsync({
            riskAssessmentIds: scenarios.map((scenario) => scenario.id),
            ...planForm.toSavePayload(),
        });
    };

    return {
        planForm,
        scenarios,
        removeScenario: props.onRemoveScenario,
        isValid,
        inProgress: saveMutation.isLoading,
        onSave,
        consequenceOptions,
        probabilityOptions,
    };
}
